import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Experiment } from '@amplitude/experiment-js-client';
import * as Sentry from '@sentry/react';

import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import awsConfig from '../../aws-exports';

const FeatureFlagContext = createContext(null);

/**
 * Amplitude Experiment feature flags for staff-fe (#35773). Fail-closed: a missing deployment
 * key, a fetch failure, or an Amplitude outage leaves variants empty and every gated feature
 * on its fallback.
 */
export const FeatureFlagProvider = ({ children }) => {
  const { loading, user, isAgency } = useSession();
  const [isReady, setIsReady] = useState(false);
  const [isEvaluated, setIsEvaluated] = useState(false);
  const clientRef = useRef(null);
  // Must mirror the Segment identity from analytics.js (`staff-${username}`) verbatim —
  // per-user inclusions in Amplitude match on this exact string.
  const experimentUserId = user?.username ? `staff-${user.username}` : undefined;

  useEffect(() => {
    const deploymentKey = awsConfig.amplitudeStaffDeploymentKey;
    if (!deploymentKey || loading || !experimentUserId) {
      return;
    }

    if (!clientRef.current) {
      clientRef.current = Experiment.initialize(deploymentKey, {
        exposureTrackingProvider: {
          // only the scalar fields: exposures also carry object-valued metadata, which
          // staff-fe's track() drops with a Sentry warning per event
          track: ({ flag_key, variant, experiment_key }) => track('$exposure', { flag_key, variant, experiment_key })
        },
        fetchOnStart: false,
        // the default (false) swallows fetch/start failures, which would mark a failed
        // session as evaluated and never reach the catches below
        throwOnError: true
      });
    }
    const client = clientRef.current;

    // the SDK restores variants from localStorage (keyed by deployment, not user) — nothing
    // may serve until this session's fetch succeeds
    setIsEvaluated(false);

    const experimentUser = {
      user_id: experimentUserId,
      user_properties: {
        environment: awsConfig.stackeryEnvironmentName,
        isAgency: !!isAgency
      }
    };
    client.setUser(experimentUser);

    client
      .fetch()
      .then(() => setIsEvaluated(true))
      .catch((error) => {
        console.error('Failed to fetch feature flags:', error);
        Sentry.captureException(error);
      });

    client
      .start(experimentUser)
      .then(() => setIsReady(true))
      .catch(() => setIsReady(false));
  }, [loading, experimentUserId, isAgency]);

  // variant() triggers automatic exposure tracking via exposureTrackingProvider.
  const evaluateFlag = useCallback(
    (key) => {
      if (!clientRef.current || !isReady || !isEvaluated) {
        return undefined;
      }
      return clientRef.current.variant(key);
    },
    [isReady, isEvaluated]
  );

  return (
    <FeatureFlagContext.Provider value={{ isReady, isEvaluated, evaluateFlag }}>{children}</FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }
  return context;
};
